import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SuperAdminSidebar from "../components/SuperAdminSidebar";
import api from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Power, Trash2, ExternalLink, Save, Crown, Sparkles, Gift, X } from "lucide-react";

export default function SuperAdminRestaurantDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [list, setList] = useState([]);
    const [restaurant, setRestaurant] = useState(null);
    const [form, setForm] = useState(null);
    const [newPw, setNewPw] = useState("");
    const [bonusDelta, setBonusDelta] = useState(0);
    const [aiDelta, setAiDelta] = useState(0);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        const r = await api.get("/superadmin/restaurants");
        setList(r.data);
        const found = r.data.find((x) => x.restaurant_id === id);
        setRestaurant(found || null);
        if (found) setForm({
            name: found.name, owner_email: found.owner_email, phone: found.phone || "",
            address: found.address || "", opening_hours: found.opening_hours || "",
            description: found.description || "",
            commission_per_cover_cents: found.commission_per_cover_cents ?? 100,
            bonus_covers_remaining: found.bonus_covers_remaining ?? 0,
            billing_threshold_cents: found.billing_threshold_cents ?? 5000,
        });
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const payload = {
                name: form.name,
                owner_email: form.owner_email,
                phone: form.phone,
                address: form.address,
                opening_hours: form.opening_hours,
                description: form.description,
                commission_per_cover_cents: form.commission_per_cover_cents,
                billing_threshold_cents: form.billing_threshold_cents,
            };
            await api.patch(`/superadmin/restaurants/${id}`, payload);
            toast.success("Aggiornato");
            load();
        } catch { toast.error("Errore"); } finally { setSaving(false); }
    };

    const resetPw = async (e) => {
        e.preventDefault();
        if (newPw.length < 6) { toast.error("Min 6 caratteri"); return; }
        try {
            await api.post(`/superadmin/restaurants/${id}/reset-password`, { new_password: newPw });
            toast.success("Password aggiornata");
            setNewPw("");
        } catch { toast.error("Errore"); }
    };

    const toggleActive = async () => {
        try {
            await api.patch(`/superadmin/restaurants/${id}`, { active: !restaurant.active });
            toast.success(restaurant.active ? "Disattivato" : "Attivato");
            load();
        } catch { toast.error("Errore"); }
    };

    const addBonus = async (e) => {
        e.preventDefault();
        const n = parseInt(bonusDelta, 10);
        if (!n || n === 0) { toast.error("Inserisci un numero diverso da 0"); return; }
        try {
            await api.post(`/superadmin/restaurants/${id}/bonus`, { add_covers: n });
            toast.success(n > 0 ? `+${n} coperti bonus aggiunti` : `${n} coperti bonus rimossi`);
            setBonusDelta(0);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    const giftAI = async (e) => {
        e.preventDefault();
        const n = parseInt(aiDelta, 10);
        if (!n || n === 0) { toast.error("Inserisci un numero diverso da 0"); return; }
        try {
            const { data } = await api.post(`/superadmin/restaurants/${id}/ai-parse-gift`, { add_credits: n });
            toast.success(n > 0 ? `+${n} upload AI regalati (totale ${data.ai_parse_bank})` : `${n} upload AI rimossi (totale ${data.ai_parse_bank})`);
            setAiDelta(0);
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        }
    };

    const remove = async () => {
        if (!window.confirm(`Eliminare definitivamente "${restaurant.name}"? Questa azione elimina TUTTI i dati (prenotazioni, tavoli, slot).`)) return;
        try {
            await api.delete(`/superadmin/restaurants/${id}`);
            toast.success("Eliminato");
            navigate("/adminrenova/restaurants");
        } catch { toast.error("Errore"); }
    };

    if (!restaurant || !form) {
        return (
            <div className="flex min-h-screen bg-ink">
                <SuperAdminSidebar />
                <main className="flex-1 p-10 text-gray-500">Caricamento...</main>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen bg-ink" data-testid="superadmin-restaurant-detail">
            <SuperAdminSidebar />
            <main className="flex-1 p-10 space-y-8">
                <button onClick={() => navigate("/adminrenova/restaurants")} className="flex items-center gap-2 text-gray-400 hover:text-gold text-xs uppercase tracking-widest">
                    <ArrowLeft size={14} /> Tutti i ristoranti
                </button>

                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="text-gold text-xs tracking-[0.4em] uppercase">Gestione</div>
                        <h1 className="font-serif text-5xl text-white mt-2">{restaurant.name}</h1>
                        <div className="flex gap-4 mt-3 text-xs text-gray-500">
                            <span>Slug: <code className="text-gold">/r/{restaurant.slug}</code></span>
                            <span>Owner: <code className="text-white">{restaurant.owner_username}</code></span>
                            <span>Prenotazioni: <strong className="text-white">{restaurant.reservations_count}</strong></span>
                        </div>
                    </div>
                    <div className="flex gap-2">
                        <a href={`/r/${restaurant.slug}`} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gold px-4 py-2.5 border border-white/10 rounded-sm">
                            <ExternalLink size={14} /> Visita pagina
                        </a>
                        <button onClick={toggleActive}
                            data-testid="toggle-active-btn"
                            className={`inline-flex items-center gap-1 text-xs px-4 py-2.5 rounded-sm border ${restaurant.active ? "border-amber-500/30 text-amber-400 hover:bg-amber-500/10" : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"}`}>
                            <Power size={14} /> {restaurant.active ? "Disattiva" : "Attiva"}
                        </button>
                    </div>
                </div>

                {/* Settings */}
                <form onSubmit={save} className="bg-ink-surface border border-white/10 rounded-sm p-8 space-y-5">
                    <h2 className="font-serif text-2xl text-white">Dati ristorante</h2>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Nome"><Input value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" /></Field>
                        <Field label="Email owner"><Input type="email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} testid="edit-email" /></Field>
                        <Field label="Telefono"><Input value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" /></Field>
                        <Field label="Orari"><Input value={form.opening_hours} onChange={(v) => setForm({ ...form, opening_hours: v })} testid="edit-hours" /></Field>
                    </div>
                    <Field label="Indirizzo"><Input value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="edit-address" /></Field>
                    <Field label="Descrizione">
                        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            data-testid="edit-description"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none resize-none" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-4 pt-3 border-t border-white/5">
                        <Field label="Commissione per coperto">
                            <select value={form.commission_per_cover_cents}
                                onChange={(e) => setForm({ ...form, commission_per_cover_cents: parseInt(e.target.value, 10) })}
                                data-testid="edit-commission"
                                className="w-full px-3 py-2.5 bg-ink border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none">
                                <option value={100}>€ 1,00</option>
                                <option value={200}>€ 2,00</option>
                                <option value={300}>€ 3,00</option>
                                <option value={400}>€ 4,00</option>
                                <option value={500}>€ 5,00</option>
                            </select>
                        </Field>
                        <Field label="Coperti bonus residui">
                            <div className="px-3 py-2.5 bg-ink border border-white/10 rounded-sm text-gold font-serif text-xl" data-testid="bonus-covers-display">
                                {restaurant.bonus_covers_remaining ?? 0}
                            </div>
                        </Field>
                        <Field label="Soglia fatturazione commissioni (€)">
                            <input type="number" min="10" max="500" step="10"
                                value={(form.billing_threshold_cents ?? 5000) / 100}
                                onChange={(e) => setForm({ ...form, billing_threshold_cents: Math.round(parseFloat(e.target.value || "0") * 100) })}
                                data-testid="edit-billing-threshold"
                                className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                        </Field>
                        <Field label="Commissioni maturate">
                            <div className={`px-3 py-2.5 bg-ink border rounded-sm font-serif text-xl ${restaurant.billing_locked ? "border-red-500/40 text-red-400" : "border-white/10 text-gold"}`}
                                data-testid="accrued-display">
                                € {((restaurant.accrued_commission_cents ?? 0) / 100).toFixed(2)}
                                {restaurant.billing_locked && <span className="text-[10px] uppercase tracking-widest ml-2">· Bloccato</span>}
                            </div>
                        </Field>
                    </div>
                    <button type="submit" disabled={saving} data-testid="save-restaurant-btn"
                        className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm disabled:opacity-50">
                        <Save size={14} /> Salva modifiche
                    </button>
                </form>

                {/* Courtesy grants */}
                <CourtesyGrantsSection restaurant={restaurant} reload={load} />

                {/* Bonus covers */}
                {/* Billing info (sola lettura per SuperAdmin) */}
                <BillingInfoReadOnly bi={restaurant.billing_info} />

                <form onSubmit={addBonus} className="bg-ink-surface border border-white/10 rounded-sm p-8">
                    <h2 className="font-serif text-2xl text-white mb-2">Gestione coperti bonus</h2>
                    <p className="text-xs text-gray-500 mb-4">Renova decide caso per caso quanti coperti regalare. Numeri positivi aggiungono, negativi sottraggono. I bonus vengono consumati per primi sulle prenotazioni senza generare commissione.</p>
                    <div className="flex flex-wrap gap-2 items-end">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">Modifica</label>
                            <input type="number" value={bonusDelta} onChange={(e) => setBonusDelta(e.target.value)}
                                data-testid="bonus-delta-input" placeholder="es. 10 o -5"
                                className="mt-2 w-48 px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                        </div>
                        <button type="submit" data-testid="bonus-apply-btn"
                            className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 py-2.5 rounded-sm hover:bg-gold-light tracking-widest uppercase text-xs whitespace-nowrap">
                            Applica
                        </button>
                        <div className="text-xs text-gray-500 ml-3">Residuo attuale: <strong className="text-gold">{restaurant.bonus_covers_remaining ?? 0}</strong> coperti</div>
                    </div>
                </form>

                {/* AI Menu Upload credits gift */}
                <form onSubmit={giftAI} className="bg-gradient-to-br from-violet-500/5 to-transparent border border-violet-400/30 rounded-sm p-8">
                    <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-sm bg-violet-500/15 border border-violet-400/40 flex items-center justify-center">
                            <Sparkles size={18} className="text-violet-300" strokeWidth={1.6} />
                        </div>
                        <h2 className="font-serif text-2xl text-white">Regala upload AI menu</h2>
                    </div>
                    <p className="text-xs text-gray-400 mb-4 max-w-2xl leading-relaxed">
                        Regala al ristoratore <strong className="text-violet-200">upload extra dell'AI Menu Parser</strong> (caricamento PDF/foto del menu → estrazione automatica dei piatti). Questi crediti <strong className="text-violet-200">non scadono mai</strong> e vengono consumati solo dopo aver esaurito i 5 upload gratuiti mensili. Numeri positivi aggiungono, negativi sottraggono. Il credito NON viene scalato in caso di errore AI.
                    </p>
                    <div className="flex flex-wrap gap-2 items-end">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">Quantità</label>
                            <input type="number" value={aiDelta} onChange={(e) => setAiDelta(e.target.value)}
                                data-testid="ai-gift-delta-input" placeholder="es. 100 o -20"
                                className="mt-2 w-48 px-3 py-2.5 bg-transparent border border-violet-400/30 rounded-sm text-white focus:border-violet-400 focus:outline-none" />
                        </div>
                        <button type="submit" data-testid="ai-gift-apply-btn"
                            className="inline-flex items-center gap-2 bg-violet-500 text-white font-semibold px-5 py-2.5 rounded-sm hover:bg-violet-400 tracking-widest uppercase text-xs whitespace-nowrap">
                            <Sparkles size={13} /> Applica
                        </button>
                        <div className="text-xs text-gray-500 ml-3">
                            Crediti attuali nel pacchetto: <strong className="text-violet-300" data-testid="ai-parse-bank-display">{restaurant.ai_parse_bank ?? 0}</strong>
                        </div>
                    </div>
                </form>

                {/* Reset password */}
                <form onSubmit={resetPw} className="bg-ink-surface border border-white/10 rounded-sm p-8">
                    <h2 className="font-serif text-2xl text-white mb-4">Reset password ristoratore</h2>
                    <p className="text-xs text-gray-500 mb-4">Imposta una nuova password per l'utente <code className="text-gold">{restaurant.owner_username}</code>. Consegnala al ristoratore.</p>
                    <div className="flex gap-2 max-w-md">
                        <Input value={newPw} onChange={setNewPw} testid="new-password-input" placeholder="Nuova password (min 6)" mono />
                        <button type="submit" data-testid="reset-password-btn"
                            className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-5 rounded-sm hover:bg-gold-light tracking-widest uppercase text-xs whitespace-nowrap">
                            <KeyRound size={14} /> Resetta
                        </button>
                    </div>
                </form>

                {/* Danger */}
                <div className="bg-red-500/5 border border-red-500/30 rounded-sm p-8">
                    <h2 className="font-serif text-2xl text-red-400 mb-2">Zona pericolosa</h2>
                    <p className="text-xs text-gray-400 mb-4">L'eliminazione rimuove il ristorante, il ristoratore e tutte le prenotazioni/tavoli/slot associati. Operazione irreversibile.</p>
                    <button onClick={remove} data-testid="delete-restaurant-btn"
                        className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 px-5 py-2.5 rounded-sm hover:bg-red-500/10 text-xs uppercase tracking-widest">
                        <Trash2 size={14} /> Elimina ristorante
                    </button>
                </div>
            </main>
        </div>
    );
}

function Field({ label, children }) {
    return <label className="block"><span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span><div className="mt-2">{children}</div></label>;
}
function Input({ value, onChange, testid, type = "text", placeholder, mono }) {
    return (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            data-testid={testid}
            className={`w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white placeholder-gray-500 focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold ${mono ? "font-mono" : ""}`} />
    );
}

const PLAN_META = {
    mirror: {
        name: "Private Mirror",
        price: "€9/mese",
        icon: Sparkles,
        description: "Pagina privata dedicata col logo del ristorante, prenotazioni guest senza login Google",
    },
    pro: {
        name: "Prenova Pro",
        price: "€12/mese",
        icon: Crown,
        description: "Esperienza 100% white-label: rimuove 'Powered by Prenova by Renova360' dalla pagina privata",
    },
};

function CourtesyGrantsSection({ restaurant, reload }) {
    const subs = restaurant.subscriptions || {};
    const [reason, setReason] = useState({ mirror: "", pro: "" });
    const [busy, setBusy] = useState(null);

    const grant = async (plan) => {
        setBusy(plan);
        try {
            await api.post(`/superadmin/restaurants/${restaurant.restaurant_id}/grant`, {
                plan,
                reason: reason[plan] || "",
            });
            toast.success(`${PLAN_META[plan].name} concesso a ${restaurant.name}`);
            setReason((r) => ({ ...r, [plan]: "" }));
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusy(null);
        }
    };

    const revoke = async (plan) => {
        if (!window.confirm(`Revocare la concessione cortesia "${PLAN_META[plan].name}" a ${restaurant.name}?`)) return;
        setBusy(plan);
        try {
            await api.delete(`/superadmin/restaurants/${restaurant.restaurant_id}/grant/${plan}`);
            toast.success("Concessione revocata");
            reload();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusy(null);
        }
    };

    return (
        <div className="bg-ink-surface border border-white/10 rounded-sm p-8" data-testid="courtesy-grants-section">
            <div className="flex items-center gap-3 mb-2">
                <Gift size={20} className="text-emerald-400" strokeWidth={1.5} />
                <h2 className="font-serif text-2xl text-white">Concessioni cortesia</h2>
            </div>
            <p className="text-xs text-gray-500 mb-5">
                Attiva o revoca i piani <strong className="text-white">Private Mirror</strong> o <strong className="text-white">Prenova Pro</strong> a titolo gratuito per questo ristorante.
                Il piano resta attivo a tempo indefinito senza addebiti su Stripe finché non lo revochi qui.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
                {["mirror", "pro"].map((plan) => {
                    const meta = PLAN_META[plan];
                    const sub = subs[plan] || {};
                    const isCourtesy = !!sub.manual_grant;
                    const isPaid = sub.status && !sub.manual_grant;
                    const Icon = meta.icon;
                    return (
                        <div
                            key={plan}
                            className={`relative border rounded-sm p-5 ${
                                isCourtesy ? "border-emerald-500/40 bg-emerald-500/[0.04]" : "border-white/10 bg-white/[0.02]"
                            }`}
                            data-testid={`grant-card-${plan}`}
                        >
                            <div className="flex items-start gap-3">
                                <Icon size={18} className={isCourtesy ? "text-emerald-300 mt-0.5" : "text-gold mt-0.5"} strokeWidth={1.5} />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="font-semibold text-white">{meta.name}</h3>
                                        <span className="text-[10px] text-gray-500">{meta.price}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-400 mt-1">{meta.description}</p>
                                </div>
                            </div>

                            {isCourtesy && (
                                <div className="mt-4 border-t border-emerald-500/20 pt-3 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <span className="text-[9px] uppercase tracking-[0.3em] bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 px-2 py-0.5 rounded-sm" data-testid={`grant-active-badge-${plan}`}>
                                            Cortesia attiva
                                        </span>
                                    </div>
                                    {sub.granted_at && (
                                        <div className="text-[10px] text-gray-500">
                                            Concesso il {new Date(sub.granted_at).toLocaleDateString("it-IT")}
                                            {sub.granted_by && <> da <code className="text-gold">{sub.granted_by}</code></>}
                                        </div>
                                    )}
                                    {sub.granted_reason && (
                                        <div className="text-[11px] text-gray-300 italic">"{sub.granted_reason}"</div>
                                    )}
                                    <button
                                        onClick={() => revoke(plan)}
                                        disabled={busy === plan}
                                        className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] font-semibold uppercase tracking-widest border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                                        data-testid={`revoke-btn-${plan}`}
                                    >
                                        <X size={12} /> Revoca
                                    </button>
                                </div>
                            )}

                            {isPaid && (
                                <div className="mt-4 border-t border-white/10 pt-3 text-[11px] text-amber-300" data-testid={`paid-sub-warning-${plan}`}>
                                    Abbonamento Stripe attivo (status: <code>{sub.status}</code>) — non sovrascrivibile da qui. Il ristoratore deve disdire dal proprio pannello.
                                </div>
                            )}

                            {!isCourtesy && !isPaid && (
                                <div className="mt-4 border-t border-white/10 pt-3 space-y-2">
                                    <input
                                        type="text"
                                        value={reason[plan]}
                                        onChange={(e) => setReason((r) => ({ ...r, [plan]: e.target.value }))}
                                        placeholder="Motivo (opzionale, es. partner storico)"
                                        data-testid={`grant-reason-input-${plan}`}
                                        className="w-full px-3 py-2 bg-transparent border border-white/15 rounded-sm text-white text-xs placeholder-gray-500 focus:border-emerald-400 focus:outline-none"
                                    />
                                    <button
                                        onClick={() => grant(plan)}
                                        disabled={busy === plan}
                                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-[11px] font-semibold uppercase tracking-widest bg-emerald-500 text-black hover:bg-emerald-400 disabled:opacity-50"
                                        data-testid={`grant-btn-${plan}`}
                                    >
                                        <Gift size={13} /> {busy === plan ? "Concedo..." : `Concedi ${meta.name} gratis`}
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function BillingInfoReadOnly({ bi }) {
    const fields = [
        { key: "legal_name", label: "Ragione sociale / Nome e Cognome" },
        { key: "vat_or_cf", label: "P.IVA / Codice Fiscale" },
        { key: "address", label: "Indirizzo" },
        { key: "email", label: "Email fatturazione" },
        { key: "pec_or_sdi", label: "PEC o Codice SDI" },
        { key: "phone", label: "Telefono" },
    ];
    const isEmpty = !bi || Object.keys(bi).length === 0;
    return (
        <div className="bg-ink-surface border border-white/10 rounded-sm p-8" data-testid="superadmin-billing-info">
            <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                <h2 className="font-serif text-2xl text-white">Dati fatturazione</h2>
                {isEmpty ? (
                    <span className="text-[10px] uppercase tracking-[0.3em] px-3 py-1 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30">Non compilati</span>
                ) : (
                    <span className="text-[10px] uppercase tracking-[0.3em] px-3 py-1 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Completi</span>
                )}
            </div>
            {isEmpty ? (
                <p className="text-xs text-gray-500">Il ristoratore non ha ancora compilato i dati di fatturazione. Sarà obbligatorio prima di attivare un piano a pagamento.</p>
            ) : (
                <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {fields.map((f) => (
                        <div key={f.key} className="border-b border-white/5 pb-2">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-0.5">{f.label}</div>
                            <div className="text-sm text-gray-100 font-mono break-all" data-testid={`bi-${f.key}`}>
                                {bi[f.key] || <span className="text-gray-600">—</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {bi?.updated_at && (
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mt-4">
                    Ultimo aggiornamento: {new Date(bi.updated_at).toLocaleString("it-IT")}
                </div>
            )}
        </div>
    );
}
